import { t, type I18nLocale } from '@ardoq/i18n';
import type { RichTextEditorLabels } from './RichTextEditor';

export const buildRichTextEditorLabels = (
  i18nLocale: I18nLocale
): RichTextEditorLabels => ({
  toolbar: {
    moreElements: t(
      i18nLocale,
      'richTextEditor:toolbar.moreElements',
      'More elements'
    ),
    textFormatting: t(
      i18nLocale,
      'richTextEditor:toolbar.textFormatting',
      'Text Formatting'
    ),
    listFormatting: t(
      i18nLocale,
      'richTextEditor:toolbar.listFormatting',
      'List Formatting'
    ),
    items: {
      textStyles: t(
        i18nLocale,
        'richTextEditor:toolbar.items.textStyles',
        'Text Styles'
      ),
      normal: t(i18nLocale, 'richTextEditor:toolbar.items.normal', 'Normal'),
      heading: (level: number) =>
        t(
          i18nLocale,
          'richTextEditor:toolbar.items.heading',
          { level },
          'Heading {{level}}'
        ),
      bold: t(i18nLocale, 'richTextEditor:toolbar.items.bold', 'Bold'),
      italic: t(i18nLocale, 'richTextEditor:toolbar.items.italic', 'Italic'),
      strike: t(i18nLocale, 'richTextEditor:toolbar.items.strike', 'Strike'),
      divider: t(i18nLocale, 'richTextEditor:toolbar.items.divider', 'Divider'),
      horizontalRule: t(
        i18nLocale,
        'richTextEditor:toolbar.items.horizontalRule',
        'Horizontal Rule'
      ),
      color: t(i18nLocale, 'richTextEditor:toolbar.items.color', 'Color'),
      blockquote: t(
        i18nLocale,
        'richTextEditor:toolbar.items.blockquote',
        'Blockquote'
      ),
      bulletList: t(
        i18nLocale,
        'richTextEditor:toolbar.items.bulletList',
        'Bullet List'
      ),
      numberedList: t(
        i18nLocale,
        'richTextEditor:toolbar.items.numberedList',
        'Numbered List'
      ),
      table: t(i18nLocale, 'richTextEditor:toolbar.items.table', 'Table'),
      fixTables: t(
        i18nLocale,
        'richTextEditor:toolbar.items.fixTables',
        'Fix tables'
      ),
      deleteTable: t(
        i18nLocale,
        'richTextEditor:toolbar.items.deleteTable',
        'Delete table'
      ),
      insertTable: t(
        i18nLocale,
        'richTextEditor:toolbar.items.insertTable',
        'Insert table'
      ),
      codeBlock: t(
        i18nLocale,
        'richTextEditor:toolbar.items.codeBlock',
        'Code Block'
      ),
      code: t(i18nLocale, 'richTextEditor:toolbar.items.code', 'Code'),
      redo: t(i18nLocale, 'richTextEditor:toolbar.items.redo', 'Redo'),
      undo: t(i18nLocale, 'richTextEditor:toolbar.items.undo', 'Undo'),
      presentation: t(
        i18nLocale,
        'richTextEditor:toolbar.items.presentation',
        'Presentation'
      ),
      placeholder: t(
        i18nLocale,
        'richTextEditor:toolbar.items.placeholder',
        'Placeholder'
      ),
      link: t(i18nLocale, 'richTextEditor:toolbar.items.link', 'Link'),
      ardoqLink: t(
        i18nLocale,
        'richTextEditor:toolbar.items.ardoqLink',
        'Ardoq Link'
      ),
      image: t(i18nLocale, 'richTextEditor:toolbar.items.image', 'Image'),
    },
  },
  linkForm: {
    linkLabel: t(i18nLocale, 'richTextEditor:linkForm.linkLabel', 'Link'),
    displayTextLabel: t(
      i18nLocale,
      'richTextEditor:linkForm.displayTextLabel',
      'Display text (optional)'
    ),
    insertButton: t(
      i18nLocale,
      'richTextEditor:linkForm.insertButton',
      'Insert link'
    ),
    updateButton: t(
      i18nLocale,
      'richTextEditor:linkForm.updateButton',
      'Update link'
    ),
    urlRequiredError: t(
      i18nLocale,
      'richTextEditor:linkForm.urlRequiredError',
      'Please fill in the URL field'
    ),
    urlInvalidError: t(
      i18nLocale,
      'richTextEditor:linkForm.urlInvalidError',
      'Please enter a valid URL'
    ),
  },
  linkBubbleMenu: {
    editLink: t(
      i18nLocale,
      'richTextEditor:linkBubbleMenu.editLink',
      'Edit link'
    ),
    removeLink: t(
      i18nLocale,
      'richTextEditor:linkBubbleMenu.removeLink',
      'Remove link'
    ),
    goToLink: t(
      i18nLocale,
      'richTextEditor:linkBubbleMenu.goToLink',
      'Go to link'
    ),
  },
  imageBubbleMenu: {
    small: t(i18nLocale, 'richTextEditor:imageBubbleMenu.small', 'Small'),
    medium: t(i18nLocale, 'richTextEditor:imageBubbleMenu.medium', 'Medium'),
    large: t(i18nLocale, 'richTextEditor:imageBubbleMenu.large', 'Large'),
    groupLabel: t(
      i18nLocale,
      'richTextEditor:imageBubbleMenu.groupLabel',
      'Image size'
    ),
  },
  table: {
    alignLeft: t(i18nLocale, 'richTextEditor:table.alignLeft', 'Align left'),
    alignCenter: t(
      i18nLocale,
      'richTextEditor:table.alignCenter',
      'Align center'
    ),
    alignRight: t(i18nLocale, 'richTextEditor:table.alignRight', 'Align right'),
    addColumnBefore: t(
      i18nLocale,
      'richTextEditor:table.addColumnBefore',
      'Add column before'
    ),
    addColumnAfter: t(
      i18nLocale,
      'richTextEditor:table.addColumnAfter',
      'Add column after'
    ),
    deleteColumn: t(
      i18nLocale,
      'richTextEditor:table.deleteColumn',
      'Delete column'
    ),
    addRow: t(i18nLocale, 'richTextEditor:table.addRow', 'Add row'),
    deleteTable: t(
      i18nLocale,
      'richTextEditor:table.deleteTable',
      'Delete table'
    ),
    addRowBefore: t(
      i18nLocale,
      'richTextEditor:table.addRowBefore',
      'Add row before'
    ),
    addRowAfter: t(
      i18nLocale,
      'richTextEditor:table.addRowAfter',
      'Add row after'
    ),
    deleteRow: t(i18nLocale, 'richTextEditor:table.deleteRow', 'Delete row'),
  },
  tableMenuButton: t(i18nLocale, 'richTextEditor:tableMenuButton', 'Menu'),
  presentationDialog: {
    presentationLabel: t(
      i18nLocale,
      'richTextEditor:presentationDialog.presentationLabel',
      'Presentation'
    ),
    presentationSearchPlaceholder: t(
      i18nLocale,
      'richTextEditor:presentationDialog.presentationSearchPlaceholder',
      'Type to search'
    ),
    presentationUrlLabel: t(
      i18nLocale,
      'richTextEditor:presentationDialog.presentationUrlLabel',
      'Presentation URL'
    ),
    presentationUrlPlaceholder: t(
      i18nLocale,
      'richTextEditor:presentationDialog.presentationUrlPlaceholder',
      'Presentation URL will be displayed here'
    ),
    linkTextLabel: t(
      i18nLocale,
      'richTextEditor:presentationDialog.linkTextLabel',
      'Link text'
    ),
    linkTextPlaceholder: t(
      i18nLocale,
      'richTextEditor:presentationDialog.linkTextPlaceholder',
      'Add link text'
    ),
    linkTextRequiredError: t(
      i18nLocale,
      'richTextEditor:presentationDialog.linkTextRequiredError',
      'You must add some text to represent the link'
    ),
    headerText: t(
      i18nLocale,
      'richTextEditor:presentationDialog.headerText',
      'Insert Presentation'
    ),
    insertButton: t(
      i18nLocale,
      'richTextEditor:presentationDialog.insertButton',
      'Insert'
    ),
    updateButton: t(
      i18nLocale,
      'richTextEditor:presentationDialog.updateButton',
      'Update'
    ),
    removeButton: t(
      i18nLocale,
      'richTextEditor:presentationDialog.removeButton',
      'Remove link'
    ),
    cancelButton: t(
      i18nLocale,
      'richTextEditor:presentationDialog.cancelButton',
      'Cancel'
    ),
    subtitle: t(
      i18nLocale,
      'richTextEditor:presentationDialog.subtitle',
      'Select a presentation to link into the message.'
    ),
    text: t(
      i18nLocale,
      'richTextEditor:presentationDialog.text',
      'Check your presentation’s permissions to make sure it will be accessible to your broadcast audience.'
    ),
  },
  generateDescription: {
    label: t(
      i18nLocale,
      'richTextEditor:generateDescription.label',
      'Generate description'
    ),
    tooltip: t(
      i18nLocale,
      'richTextEditor:generateDescription.tooltip',
      'Generate description'
    ),
  },
});

export type GenerateDescriptionLabels = {
  generateDescriptionTooltip: string;
  missingItemTooltip: string;
  defaultErrorMessage: string;
  generateLabel: string;
  generatingDescription: string;
  generatedByAiBadge: string;
};

export const buildGenerateDescriptionLabels = (
  i18nLocale: I18nLocale
): GenerateDescriptionLabels => ({
  generateDescriptionTooltip: t(
    i18nLocale,
    'richTextEditor:generateDescription.tooltip',
    'Generate description'
  ),
  missingItemTooltip: t(
    i18nLocale,
    'richTextEditor:generateDescription.missingItemTooltip',
    'Please provide a name to enable the AI to generate a description'
  ),
  defaultErrorMessage: t(
    i18nLocale,
    'richTextEditor:generateDescription.defaultErrorMessage',
    'An error occurred while generating the description. Please try contacting support if the issue persists.'
  ),
  generateLabel: t(
    i18nLocale,
    'richTextEditor:generateDescription.label',
    'Generate description'
  ),
  generatingDescription: t(
    i18nLocale,
    'richTextEditor:generateDescription.generating',
    'Generating description…'
  ),
  generatedByAiBadge: t(
    i18nLocale,
    'richTextEditor:generateDescription.generatedByAi',
    'Generated by AI'
  ),
});
